import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, Platform, Pressable, StyleSheet, View } from 'react-native';
import { NeutralThemeProvider, useTheme } from '@/context/ThemeContext';
import useResponsiveness from '@/hooks/useResponsiveness';
import Icon from '@/components/Icon';
import Text from '@/components/Typography/Text';
import WaitingBalloonGame from './WaitingBalloonGame';
import WaitingFlappyGame from './WaitingFlappyGame';
import WaitingSnakeGame from './WaitingSnakeGame';
import { getWaitingGame } from '@/lib/waitingGames';

const PRESETS = {
  image: { icon: 'mountain-sun', label: 'Generating your image…', hint: 'High-quality images can take a minute or two.' },
  video: { icon: 'film', label: 'Generating your video…', hint: 'Video generations can take a few minutes.' },
  audio: { icon: 'music', label: 'Generating your music…', hint: 'Songs can take a couple of minutes to compose.' },
  '3d': { icon: 'cube', label: 'Generating your 3D model…', hint: '3D model generations can take a few minutes.' },
};

const createStyles = colors => StyleSheet.create({
  container: { width: '100%', height: '100%', alignItems: 'center', justifyContent: 'center', padding: 12, gap: 8, backgroundColor: colors.solid_color_tertiary },
  status: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, maxWidth: '100%' },
  statusLabel: { flexShrink: 1 },
  elapsed: { fontVariant: ['tabular-nums'] },
  icon: { marginBottom: 16 },
  hint: { textAlign: 'center', maxWidth: 280, marginTop: 4 },
  choices: { alignItems: 'center', gap: 12, marginTop: 16, maxWidth: '100%' },
  choiceRow: { flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center', gap: 8 },
  playButton: { minHeight: 44, paddingHorizontal: 12, borderRadius: 10, borderWidth: 1, borderColor: colors.border_color_primary, backgroundColor: colors.solid_color_primary, alignItems: 'center', justifyContent: 'center' },
});

const GAME_NAMES = { balloon: 'Balloon Pop', flappy: 'Flappy Flight', snake: 'Snake' };

function WaitingContent({ mediaType, compact = false, generationKey }) {
  const { colors } = useTheme();
  const { isXS } = useResponsiveness();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const startedAt = useRef(Date.now());
  const [elapsed, setElapsed] = useState(0);
  const [showGame, setShowGame] = useState(false);
  const [game, setGame] = useState(null);
  const fallbackKey = useId();
  const desktop = Platform.OS === 'macos' || (Platform.OS === 'web' && !isXS
    && !/Android|iPhone|iPad|Mobile/i.test(globalThis.navigator?.userAgent || '')
    && !globalThis.matchMedia?.('(pointer: coarse)').matches);
  const preset = PRESETS[String(mediaType || '').toLowerCase()] || { icon: 'image', label: 'Generating media…' };

  useEffect(() => {
    const timer = setInterval(() => setElapsed((Date.now() - startedAt.current) / 1000), 500);
    return () => clearInterval(timer);
  }, []);
  useEffect(() => {
    setGame(getWaitingGame(generationKey || fallbackKey, desktop));
  }, [generationKey, fallbackKey, desktop]);
  const Game = game === 'snake' ? WaitingSnakeGame : game === 'flappy' ? WaitingFlappyGame : WaitingBalloonGame;
  const availableGames = desktop ? ['balloon', 'flappy', 'snake'] : ['balloon', 'flappy'];
  const choices = [game, ...availableGames.filter(choice => choice !== game)].filter(choice => availableGames.includes(choice));

  return (
    <View style={styles.container} testID="media-generation-waiting">
      {!showGame && <Icon name={preset.icon} size={36} color={colors.text_color_secondary} style={styles.icon} />}
      <View style={styles.status}>
        <ActivityIndicator size="small" color={colors.text_color_secondary} />
        <Text size={showGame ? 'xs' : 'n'} weight="medium" style={styles.statusLabel}>{preset.label}</Text>
        <Text size={showGame ? 'xxs' : 'xs'} color="secondary" style={styles.elapsed}>{elapsed.toFixed(1)}s</Text>
      </View>
      {showGame ? (
        <Game key={game} colors={colors} compact={compact || isXS} onHide={() => setShowGame(false)} />
      ) : (
        <>
          {elapsed >= 30 && preset.hint && !compact && <Text size="xs" color="tertiary" style={styles.hint}>{preset.hint}</Text>}
          {elapsed >= 3 && (
            <View style={styles.choices}>
              <Text size="xs" color="secondary">Play a game while you wait</Text>
              <View style={styles.choiceRow}>
                {choices.map((choice, index) => <Pressable key={choice} accessibilityRole="button" accessibilityLabel={`Choose ${GAME_NAMES[choice]}`} onPress={() => { setGame(choice); setShowGame(true); }} style={({ pressed }) => [styles.playButton, index === 0 && { backgroundColor: colors.text_color_primary, borderColor: colors.text_color_primary }, { opacity: pressed ? 0.7 : 1 }]}>
                  <Text size="s" weight="medium" style={{ color: index === 0 ? colors.accent_text_color : colors.text_color_primary }}>{GAME_NAMES[choice]}</Text>
                </Pressable>)}
              </View>
            </View>
          )}
        </>
      )}
    </View>
  );
}

function MediaGenerationWaiting(props) {
  return <NeutralThemeProvider><WaitingContent {...props} /></NeutralThemeProvider>;
}

export default React.memo(MediaGenerationWaiting);
